import { Component, OnInit, Output, EventEmitter, Input, OnChanges, SimpleChanges, ViewChild, ElementRef, HostListener, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-email-input',
  templateUrl: './email-input.component.html',
  styleUrls: ['./email-input.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EmailInputComponent),
      multi: true
    }
  ]
})
export class EmailInputComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() initialEmails?: string[] = [];
  @Input() label: string = '';
  @Input() disabled = false;
  @Output() emailsChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() hasErrorChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('emailInput') emailInput!: ElementRef<HTMLDivElement>;
  @ViewChild('emailInputContainer', { static: true }) emailInputContainer!: ElementRef<HTMLDivElement>;

  emails: string[] = [];

  hasError: boolean = false;
  errorMessage: string = '';

  private emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  private delimiters: string[] = ['Enter', ',', ';', ' '];

  private isResizing: boolean = false;
  private lastDownY: number = 0;
  private initialHeight: number = 0;
  private minContentHeight: number = 0;

  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(public translateService: TranslateService) {}

  ngOnInit() {
    this.emails = [...(this.initialEmails || []).map(email => email.toLowerCase())];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialEmails'] && !changes['initialEmails'].firstChange) {
      this.emails = [...(this.initialEmails || []).map(email => email.toLowerCase())];
      this.emailsChange.emit(this.emails);
    }
  }

  addEmail(email?: string) {
    const emailToAdd = email !== undefined ? email.trim() : this.getCurrentInput().trim();

    if (this.hasEmailErrors(emailToAdd)) {
      return;
    }

    if (emailToAdd) {
      this.emails.push(emailToAdd.toLowerCase());
      this.onChange(this.emails);
      this.emailsChange.emit(this.emails);
      this.clearInput();
    }
  }

  removeEmail(index: number) {
    this.emails.splice(index, 1);
    this.onChange(this.emails);
    this.emailsChange.emit(this.emails);
  }

  hasEmailErrors(email?: string) {
    if (email) {
      if (!this.emailPattern.test(email)) {
        this.hasError = true;
        this.errorMessage = this.translateService.instant('shared.invalidEmailFormat');
        this.hasErrorChange.emit(this.hasError);
        return true;
      }
      if (this.emails.includes(email.toLowerCase())) {
        this.hasError = true;
        this.errorMessage = this.translateService.instant('shared.emailAlreadyExists');
        this.hasErrorChange.emit(this.hasError);
        return true;
      }
    }
    this.hasError = false;
    this.errorMessage = '';
    this.hasErrorChange.emit(this.hasError);
    return false;
  }

  onKeydown(event: KeyboardEvent) {
    this.hasErrorChange.emit(this.hasError);
    if (this.delimiters.includes(event.key)) {
      event.preventDefault();
      const text = this.getCurrentInput().trim();
      if (text) {
        this.addEmail(text);
      }
    } else if (event.key === 'Backspace' && this.getCurrentInput() === '') {
      this.emails.pop();
      this.onChange(this.emails);
      this.emailsChange.emit(this.emails);
    } else {
      if (this.hasError) {
        this.hasError = false;
        this.errorMessage = '';
      }
    }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pasteData = event.clipboardData?.getData('text') || '';
    const emails = pasteData.split(/[\s,;]+/).filter(email => email);
    emails.forEach(email => this.addEmail(email));
  }

  getCurrentInput(): string {
    return this.emailInput.nativeElement.innerText;
  }

  clearInput() {
    this.emailInput.nativeElement.innerText = '';
  }

  focusInput() {
    this.emailInput.nativeElement.focus();
  }

  initResize(event: MouseEvent) {
    event.preventDefault();
    this.isResizing = true;
    this.lastDownY = event.clientY;
    this.initialHeight = this.emailInputContainer.nativeElement.offsetHeight;

    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.stopResize);
  }

  onMouseMove = (event: MouseEvent) => {
    if (!this.isResizing) return;

    const deltaY = event.clientY - this.lastDownY;
    const newHeight = Math.max(this.minContentHeight, this.initialHeight + deltaY, 50);

    this.emailInputContainer.nativeElement.style.minHeight = `${newHeight}px`;
  };

  stopResize = () => {
    if (this.isResizing) {
      this.isResizing = false;
      document.removeEventListener('mousemove', this.onMouseMove);
      document.removeEventListener('mouseup', this.stopResize);
    }
  };

  @HostListener('window:touchmove', ['$event'])
  onTouchMove(event: TouchEvent) {
    if (this.isResizing) {
      const touch = event.touches[0];
      const deltaY = touch.clientY - this.lastDownY;
      const newHeight = Math.max(this.minContentHeight, this.initialHeight + deltaY, 50);

      this.emailInputContainer.nativeElement.style.minHeight = `${newHeight}px`;
    }
  }

  @HostListener('window:touchend', ['$event'])
  onTouchEnd(event: TouchEvent) {
    if (this.isResizing) {
      this.isResizing = false;
    }
  }

  writeValue(value: string[]): void {
    this.emails = value ? [...value] : [];
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }
}
